import { useEffect } from "react";

/**
 * ドロップダウンのタッチスクロール問題を修正するカスタムフック
 *
 * ハイブリッドアプローチ: touch-action + touchmove preventDefault
 *
 * 戦略:
 * 1. MutationObserverでドロップダウンの出現を検知
 * 2. ドロップダウン表示時にbodyのtouch-actionをnoneに設定
 *    → ブラウザのスクロール予測を早期に阻止（CSS層で対策）
 * 3. touchstartではpreventDefault()しない（クリック/タップを許可）
 * 4. touchmoveで境界条件のみpreventDefault()
 *
 * これにより:
 * - 選択肢のタップが正常に動作
 * - ページスクロールは発生しない
 * - ドロップダウン内のスクロールは正常動作
 */
const useDropdownTouchFix = () => {
  useEffect(() => {
    let startY = 0;
    let currentDropdown = null;

    // ドロップダウンの存在をチェックしてtouch-actionを設定
    const updateTouchAction = () => {
      const dropdowns = document.querySelectorAll(".ant-select-dropdown");
      const hasVisibleDropdown = Array.from(dropdowns).some(dropdown => {
        const style = window.getComputedStyle(dropdown);
        return style.display !== "none" && style.visibility !== "hidden";
      });

      if (hasVisibleDropdown) {
        // ドロップダウン表示中はbodyのスクロールを無効化
        document.body.style.touchAction = "none";
      } else {
        document.body.style.touchAction = "";
      }
    };

    // DOMの変更を監視してtouch-actionを更新
    const observer = new MutationObserver(() => {
      requestAnimationFrame(updateTouchAction);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    // タッチ開始時の処理（preventDefault()しない → クリック許可）
    const handleTouchStart = e => {
      const dropdown = e.target.closest(".ant-select-dropdown");

      if (dropdown) {
        startY = e.touches[0].clientY;
        currentDropdown = dropdown;
      } else {
        currentDropdown = null;
      }
    };

    // タッチ移動時の処理
    const handleTouchMove = e => {
      if (!currentDropdown) return;

      const dropdown = e.target.closest(".ant-select-dropdown");
      if (!dropdown) {
        currentDropdown = null;
        return;
      }

      const scrollable = dropdown.querySelector(".rc-virtual-list-holder");

      if (!scrollable) {
        // スクロール領域がない場合はページスクロールを完全に防止
        e.preventDefault();
        return;
      }

      const currentY = e.touches[0].clientY;
      const deltaY = startY - currentY;

      const { scrollTop, scrollHeight, clientHeight } = scrollable;
      const isAtTop = scrollTop <= 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

      const scrollingUp = deltaY > 0;
      const scrollingDown = deltaY < 0;

      if ((isAtTop && scrollingDown) || (isAtBottom && scrollingUp)) {
        // 境界に達していてさらにスクロールしようとしている場合
        e.preventDefault();
      }

      startY = currentY;
    };

    // タッチ終了時のクリーンアップ
    const handleTouchEnd = () => {
      currentDropdown = null;
      startY = 0;
    };

    // touchstartはpassive: true（preventDefault不要なので）
    document.addEventListener("touchstart", handleTouchStart, {
      passive: true,
      capture: true,
    });

    document.addEventListener("touchmove", handleTouchMove, {
      passive: false,
      capture: true,
    });

    document.addEventListener("touchend", handleTouchEnd, {
      passive: true,
      capture: true,
    });

    // 初期チェック
    updateTouchAction();

    // クリーンアップ
    return () => {
      observer.disconnect();
      document.removeEventListener("touchstart", handleTouchStart, {
        capture: true,
      });
      document.removeEventListener("touchmove", handleTouchMove, {
        capture: true,
      });
      document.removeEventListener("touchend", handleTouchEnd, {
        capture: true,
      });
      document.body.style.touchAction = "";
    };
  }, []);
};

export default useDropdownTouchFix;
